import pb, {
  BlurOptions,
  FormatOptions,
  GravityType,
  ParamBuilder,
  QualityOptions,
  ResizeType,
} from '@bitpatty/imgproxy-url-builder';
import { ImageLoaderProps } from 'next/image';
import { UNIVERSAL_ENV_VARS } from '../../constants/environmentVariables';
import { getCloudfrontLoader } from './cloudfront';

export type imgProxyLoader = {
  customLoaderType: 'imgProxy';
  origWidth: number;
  origHeight?: number;
  customLoaderFilters?: imgProxyFilters;
};

interface imgProxyFilters {
  blur?: BlurOptions;
  quality?: QualityOptions;
  format?: FormatOptions;
  reziseType?: ResizeType;
  gravityType?: GravityType;
}

const aspectRatioHeight = (width: number, origWidth: number, origHeight = 0): number => {
  return origWidth === 0 ? 0 : Math.round((origHeight / origWidth) * width);
};

export const getImgProxyLoader = (
  src: string,
  width: number,
  origWidth: number,
  origHeight = 0,
  filters: imgProxyFilters = {},
): string => {
  const urlParts = src.split('/');
  const hostName = urlParts[2];
  const height = aspectRatioHeight(width, origWidth, origHeight);

  if (!hostName) {
    return src;
  }

  // Backwards compatibility for old image urls
  if (hostName.includes('cloudfront.net')) {
    return getCloudfrontLoader(src, width, origWidth, origHeight, filters);
  }

  if (!src.includes('plain/') && !src.includes('enc/')) {
    return src;
  }

  const parts = src.includes('plain/') ? src.split('plain/') : src.split('enc/');
  const pathName = parts[parts.length - 1];

  const addFilters = (pb: ParamBuilder): ParamBuilder => {
    if (filters.blur) {
      pb.blur(filters.blur);
    }
    if (filters.format) {
      pb.format(filters.format);
    } else {
      pb.format('webp');
    }

    if (filters.quality) {
      pb.quality(filters.quality);
    }
    return pb;
  };

  const paramBuilder = pb()
    .resize({
      type: filters.reziseType ?? ResizeType.FILL_DOWN,
      width: width,
      height: height,
    })
    .gravity({
      type: filters.gravityType ?? GravityType.SMART,
    });

  const imgProxySrc = addFilters(paramBuilder).build({
    plain: true,
    path: pathName,
    baseUrl: `https://${hostName}`,
    signature: {
      key: UNIVERSAL_ENV_VARS.IMGPROXY_KEY,
      salt: UNIVERSAL_ENV_VARS.IMGPROXY_SALT,
    },
  });

  return imgProxySrc;
};

export function createImgProxyLoader(
  origWidth: number,
  origHeight = 0,
  filters: imgProxyFilters = {},
) {
  return ({ src, width, quality }: ImageLoaderProps) =>
    getImgProxyLoader(src, width, origWidth, origHeight, { quality, ...filters });
}
